/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type ContentStackTermsAndConditions } from '../../interface';
import styles from './CMSTermsAndConditions.module.scss';
import CMSTableOfContents from './CMSTableOfContents';
import ScrollToTopButton from '@/components/ScrollToTopButton/ScrollToTopButton';
import { View } from '@az/starc-ui';
import { memo } from 'react';
import { useCMSRichTextModal } from '../../hooks/useCMSRichTextModal';

export type Props = {
  content: ContentStackTermsAndConditions;
};

function CMSTermsSection({ section }: { section: { section_id: string; content: string } }) {
  const { processedHTML, handleClick, ModalComponents } = useCMSRichTextModal(section.content);

  const ultraProcessedHTML = processedHTML
    .replace(/<table/g, `<div class="${styles.tableWrapper}"><table`)
    .replace(/<\/table>/g, `</table></div>`);

  return (
    <div id={section.section_id.replace('#', '')}>
      <div
        className={styles.paragraph}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: ultraProcessedHTML }}
      />
      <ModalComponents />
    </div>
  );
}

const CMSTermsAndConditions = memo(({ content }: Props) => {
  const paragraphs = content.content;

  return (
    <View className={styles.page}>
      <View>
        <div
          className={styles.pageDescriptionMobile}
          dangerouslySetInnerHTML={{ __html: content.page_description }}
        />
      </View>
      <View className={styles.container}>
        {content.menu_section.menu_group.length > 0 && (
          <View className={styles.tableOfContentsContainer}>
            <CMSTableOfContents groups={content.menu_section.menu_group} />
          </View>
        )}
        <View className={styles.contentContainer}>
          <View>
            <div
              className={styles.pageDescription}
              dangerouslySetInnerHTML={{ __html: content.page_description }}
            />
          </View>
          {paragraphs.map((paragraph, index) => (
            <View key={`${paragraph.section_id}-${index}`} className={styles.paragraphsContainer}>
              <h2 className={styles.paragraphHeading} id={paragraph.section_id?.replace('#', '')}>
                {paragraph.heading}
              </h2>
              {paragraph.group_content.map((section) => (
                <CMSTermsSection key={section.section_id} section={section} />
              ))}
            </View>
          ))}
        </View>
      </View>
      <ScrollToTopButton />
    </View>
  );
});

CMSTermsAndConditions.displayName = 'CMSTermsAndConditions';

export default CMSTermsAndConditions;
